import React, { memo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { Button } from '@xsolla/ui-core';
import { Checkmark, Pencil } from '@xsolla/ui-icons';

import { postUserAttribute, postUserReadonlyAttribute } from '../../../api';
import { UserAttribute, UserAttributes } from '../../../api/users/attributes/types';
import { useConfig } from '../../../contexts/config';
import { useOnClickOutside } from '../../../hooks';
import { useNotifications } from '../../../hooks/useNotifications';
import {
  EditValueWrapper,
  ValueInput,
  ValueInputWrapper,
  ValueSaveButton,
  ValueSelect,
  ValueWrapper
} from './styles';

type Props = {
  attribute: UserAttribute;
  userId: string;
  setData: React.Dispatch<React.SetStateAction<UserAttributes>>;
};

const ChangeValue = ({ attribute, userId, setData }: Props): JSX.Element => {
  const intl = useIntl();
  const ref = useRef<HTMLDivElement>(null);
  const { merchantId } = useConfig();
  const { showErrorNotify, showSuccessNotify } = useNotifications();
  const { value, readonly, key, data_type, tableKey } = attribute;

  const [newValue, setNewValue] = useState<string>(value || '');
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [fetching, setFetching] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<string>('');

  useOnClickOutside(ref, (event) => {
    // Hack for select options
    if (
      (event.target as HTMLElement)?.innerText === 'TRUE' ||
      (event.target as HTMLElement)?.innerText === 'FALSE'
    ) {
      return;
    }
    setNewValue(value || '');
    setValidationError('');
    setFetching(false);
    setIsEdit(false);
  });

  const validate = (): boolean => {
    setValidationError('');

    if (!newValue) {
      setValidationError(intl.formatMessage({ id: 'app.validation.required' }));
      return false;
    }

    if (data_type === 'integer') {
      setValidationError(
        intl.formatMessage({ id: 'users.attributes.table.type-error' }, { type: 'integer' })
      );
      return new RegExp(/^-?\d+$/).test(newValue);
    }

    if (data_type === 'number') {
      setValidationError(
        intl.formatMessage({ id: 'users.attributes.table.type-error' }, { type: 'number' })
      );
      return new RegExp(/^-?\d*\.?\d+$/).test(newValue);
    }

    return true;
  };

  const updateTableValue = (attrValue: string) => {
    setData((prevValue) => {
      return prevValue.map((attr) => {
        if (attr.tableKey === tableKey) {
          return {
            ...attr,
            value: attrValue
          };
        }
        return attr;
      });
    });
  };

  const handleSave = async () => {
    if (!validate()) {
      return;
    }

    try {
      setValidationError('');
      setFetching(true);

      if (readonly) {
        await postUserReadonlyAttribute(userId, {
          publisher_id: merchantId,
          publisher_project_id: attribute.publisher_project_id,
          attributes: [
            {
              key,
              value: newValue,
              attr_type: attribute.attr_type,
              permission: attribute.permission
            }
          ]
        });

        showSuccessNotify();
        updateTableValue(newValue);
        setIsEdit(false);
        return;
      }

      await postUserAttribute(userId, {
        publisher_id: merchantId,
        attributes: [
          { key, value: newValue, attr_type: attribute.attr_type, permission: attribute.permission }
        ]
      });

      showSuccessNotify();
      updateTableValue(newValue);
      setIsEdit(false);
    } catch (error) {
      setFetching(false);
      setIsEdit(false);
      showErrorNotify(error);
    }
  };

  if (isEdit) {
    return (
      <EditValueWrapper ref={ref}>
        <ValueInputWrapper isError={!!validationError}>
          {data_type === 'boolean' ? (
            <ValueSelect
              value={newValue}
              onChange={(selectValue) => selectValue && setNewValue(selectValue as string)}
              errorMessage={validationError}
              options={[
                {
                  children: 'TRUE',
                  value: 'true'
                },
                {
                  children: 'FALSE',
                  value: 'false'
                }
              ]}
            />
          ) : (
            <ValueInput
              value={newValue}
              onChange={(event) => setNewValue(event.target.value)}
              errorMessage={validationError}
            />
          )}
        </ValueInputWrapper>
        <ValueSaveButton
          look="icon"
          palette="action"
          size="md"
          onClick={handleSave}
          fetching={fetching}>
          <Checkmark size={20} color="#00c77e" />
        </ValueSaveButton>
      </EditValueWrapper>
    );
  }

  return (
    <>
      <ValueWrapper>{value}</ValueWrapper>
      <Button look="icon" palette="brand" size="md" onClick={() => setIsEdit(true)}>
        <Pencil size={20} color="#0073F7" />
      </Button>
    </>
  );
};

export default memo(ChangeValue);
